import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { CodeEditor } from '../components/CodeEditor';
import { Play, RotateCcw, Download, Copy, Check, Zap, ChevronDown, FileCode, Lock, X, Square, GripHorizontal, Terminal, AlertTriangle, Sparkles, Shuffle, Loader2, Printer, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LANGUAGE_OPTIONS = [
  { value: 'javascript', label: 'JavaScript', ext: '.js', runnable: true },
  { value: 'typescript', label: 'TypeScript', ext: '.ts', runnable: true },
  { value: 'python', label: 'Python', ext: '.py', runnable: true },
  { value: 'go', label: 'Go', ext: '.go', runnable: true },
  { value: 'rust', label: 'Rust', ext: '.rs', runnable: true },
  { value: 'c', label: 'C', ext: '.c', runnable: true },
  { value: 'cpp', label: 'C++', ext: '.cpp', runnable: true },
  { value: 'ruby', label: 'Ruby', ext: '.rb', runnable: true },
  { value: 'php', label: 'PHP', ext: '.php', runnable: true },
  { value: 'shell', label: 'Shell', ext: '.sh', runnable: true },
  { value: 'html', label: 'HTML', ext: '.html', runnable: false },
  { value: 'css', label: 'CSS', ext: '.css', runnable: false },
  { value: 'sql', label: 'SQL', ext: '.sql', runnable: false },
  { value: 'json', label: 'JSON', ext: '.json', runnable: false },
  { value: 'yaml', label: 'YAML', ext: '.yaml', runnable: false },
  { value: 'markdown', label: 'Markdown', ext: '.md', runnable: false },
];

const STARTER_TEMPLATES = {
  javascript: `// CodeFoundry Playground - JavaScript\nconsole.log("Hello from CodeFoundry!");\n\nfunction fibonacci(n) {\n  if (n <= 1) return n;\n  return fibonacci(n - 1) + fibonacci(n - 2);\n}\n\nconsole.log("fib(10) =", fibonacci(10));\n`,
  python: `# CodeFoundry Playground - Python\nprint("Hello from CodeFoundry!")\n\ndef fibonacci(n):\n    if n <= 1:\n        return n\n    return fibonacci(n - 1) + fibonacci(n - 2)\n\nprint(f"fib(10) = {fibonacci(10)}")\n`,
  typescript: `// CodeFoundry Playground - TypeScript\nconsole.log("Hello from CodeFoundry!");\n\nfunction fibonacci(n: number): number {\n  if (n <= 1) return n;\n  return fibonacci(n - 1) + fibonacci(n - 2);\n}\n\nconsole.log("fib(10) =", fibonacci(10));\n`,
  go: `package main\n\nimport "fmt"\n\nfunc fibonacci(n int) int {\n\tif n <= 1 {\n\t\treturn n\n\t}\n\treturn fibonacci(n-1) + fibonacci(n-2)\n}\n\nfunc main() {\n\tfmt.Println("Hello from CodeFoundry!")\n\tfmt.Println("fib(10) =", fibonacci(10))\n}\n`,
  rust: `fn fibonacci(n: u32) -> u32 {\n    if n <= 1 { return n; }\n    fibonacci(n - 1) + fibonacci(n - 2)\n}\n\nfn main() {\n    println!("Hello from CodeFoundry!");\n    println!("fib(10) = {}", fibonacci(10));\n}\n`,
  shell: `#!/bin/bash\necho "Hello from CodeFoundry!"\necho "Current date: $(date)"\necho "Working directory: $(pwd)"\n`,
  c: `#include <stdio.h>\n\nint fibonacci(int n) {\n    if (n <= 1) return n;\n    return fibonacci(n - 1) + fibonacci(n - 2);\n}\n\nint main() {\n    printf("Hello from CodeFoundry!\\n");\n    printf("fib(10) = %d\\n", fibonacci(10));\n    return 0;\n}\n`,
  cpp: `#include <iostream>\nusing namespace std;\n\nint fibonacci(int n) {\n    if (n <= 1) return n;\n    return fibonacci(n - 1) + fibonacci(n - 2);\n}\n\nint main() {\n    cout << "Hello from CodeFoundry!" << endl;\n    cout << "fib(10) = " << fibonacci(10) << endl;\n    return 0;\n}\n`,
  ruby: `puts "Hello from CodeFoundry!"\n\ndef fibonacci(n)\n  return n if n <= 1\n  fibonacci(n - 1) + fibonacci(n - 2)\nend\n\nputs "fib(10) = #{fibonacci(10)}"\n`,
  php: `<?php\necho "Hello from CodeFoundry!\\n";\n\nfunction fibonacci($n) {\n    if ($n <= 1) return $n;\n    return fibonacci($n - 1) + fibonacci($n - 2);\n}\n\necho "fib(10) = " . fibonacci(10) . "\\n";\n`,
};

export default function PlaygroundPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const labId = searchParams.get('lab');  // "4" | "5" | "7" | "10" | null
  const [language, setLanguage] = useState('python');
  const [filename, setFilename] = useState('playground.py');
  const [code, setCode] = useState(STARTER_TEMPLATES.python || '');
  const [completionStatus, setCompletionStatus] = useState('idle');
  const [copied, setCopied] = useState(false);
  const [autoCompleteEnabled, setAutoCompleteEnabled] = useState(true);
  const [autoCompleteAvailable, setAutoCompleteAvailable] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [usage, setUsage] = useState(null);
  const [userPlan, setUserPlan] = useState('free');
  // Lab 5 — whitelisted students never see the quota banner nor the Upgrade
  // modal; they get 5 debug runs per rolling 30 days enforced on the backend.
  const [isStudent, setIsStudent] = useState(false);

  // Execution state
  const [isRunning, setIsRunning] = useState(false);
  const [execResult, setExecResult] = useState(null);
  const [showOutput, setShowOutput] = useState(false);
  const [outputHeight, setOutputHeight] = useState(200);
  const [execUsage, setExecUsage] = useState(null);
  const [execLimits, setExecLimits] = useState(null);
  const dragRef = useRef(null);
  const codeRef = useRef(code);
  codeRef.current = code;

  // ─────────────── Lab mode (?lab=4|5|7|10) ───────────────
  // When a labId is on the URL we render a right-hand "Ask the Agent" panel
  // that hits POST /api/labs/{id}/ask — ONE OmniRoute call, no 3-agent, no
  // 16-agent workflow. The returned code snaps into this very editor so the
  // student can tap Run (existing server-side executor) and screenshot/print
  // the deliverable without leaving the page.
  const [labDef, setLabDef] = useState(null);
  const [labPrompt, setLabPrompt] = useState('');
  const [labLoading, setLabLoading] = useState(false);
  const [labResp, setLabResp] = useState(null);
  const [labError, setLabError] = useState('');

  useEffect(() => {
    if (!labId) { setLabDef(null); return; }
    fetch(`${API_URL}/api/labs/${labId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(setLabDef)
      .catch(() => setLabError('Lab not found or you do not have access.'));
  }, [labId]);

  const askLabAgent = useCallback(async () => {
    if (!labId || labPrompt.trim().length < 4) return;
    setLabLoading(true); setLabError(''); setLabResp(null);
    try {
      const r = await fetch(`${API_URL}/api/labs/${labId}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ prompt: labPrompt }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `Request failed (${r.status})`);
      setLabResp(data);
    } catch (e) {
      setLabError(e.message || 'Something went wrong.');
    } finally {
      setLabLoading(false);
    }
  }, [labId, labPrompt]);

  const loadLabStarter = useCallback(async () => {
    if (!labId) return;
    try {
      const r = await fetch(`${API_URL}/api/labs/${labId}/starter`, { credentials: 'include' });
      const d = await r.json();
      if (d.prompt) setLabPrompt(d.prompt);
    } catch {}
  }, [labId]);

  const insertLabCodeIntoEditor = useCallback(() => {
    const p = labResp?.parsed || {};
    const generated = p.code || p.fixed_code || p.visualization_code || p.prototype_code || '';
    if (!generated) return;
    setLanguage('python');
    setFilename('lab_submission.py');
    setCode(generated);
  }, [labResp]);

  const printLabDeliverable = useCallback(() => {
    if (!labResp) return;
    const p = labResp.parsed || {};
    const u = labResp.usage || {};
    const section = (title, body) => body ? `<h2>${title}</h2>${body}` : '';
    const codeBlock = (c) => c ? `<pre><code>${String(c).replace(/</g, '&lt;')}</code></pre>` : '';
    const testsTable = Array.isArray(p.tests) ? `<table><thead><tr><th>#</th><th>Category</th><th>Input/Assertion</th><th>Expected</th></tr></thead><tbody>${p.tests.map((t,i)=>`<tr><td>${i+1}</td><td>${t.category||''}</td><td><code>${(t.input||t.assertion||'').toString().replace(/</g,'&lt;')}</code></td><td>${(t.expected||t.name||'').toString().replace(/</g,'&lt;')}</td></tr>`).join('')}</tbody></table>` : '';
    const dxBlock = p.diagnosis ? `
      <h3>Syntax bug (line ${p.diagnosis.syntax_bug?.line_hint ?? '?'})</h3>
      <p><em>Symptom:</em> ${p.diagnosis.syntax_bug?.symptom || ''}<br/><em>Root cause:</em> ${p.diagnosis.syntax_bug?.root_cause || ''}</p>
      <h3>Logic bug (line ${p.diagnosis.logic_bug?.line_hint ?? '?'})</h3>
      <p><em>Symptom:</em> ${p.diagnosis.logic_bug?.symptom || ''}<br/><em>Root cause:</em> ${p.diagnosis.logic_bug?.root_cause || ''}</p>` : '';
    const chartsList = Array.isArray(p.charts) ? `<ul>${p.charts.map(c => `<li><strong>${(c.type||'chart').toUpperCase()}</strong> — ${c.question} <em>(x: ${c.axes?.x}, y: ${c.axes?.y})</em></li>`).join('')}</ul>${p.best_chart ? `<p><strong>Best chart:</strong> #${p.best_chart.index} — ${p.best_chart.justification}</p>` : ''}` : '';
    const planBlock = p.plan ? `<p><strong>Problem:</strong> ${p.plan.problem}<br/><strong>Users:</strong> ${p.plan.users}</p><ul>${(p.plan.requirements||[]).map(r=>`<li>${r}</li>`).join('')}</ul>` : '';
    const slidesBlock = Array.isArray(p.slide_outline) ? p.slide_outline.map(s => `<p><strong>Slide ${s.slide}: ${s.title}</strong></p><ul>${(s.bullets||[]).map(b=>`<li>${b}</li>`).join('')}</ul>`).join('') : '';
    const raiBlock = Array.isArray(p.responsible_ai_checklist) ? `<table><thead><tr><th>Item</th><th>Pass</th><th>Note</th></tr></thead><tbody>${p.responsible_ai_checklist.map(r=>`<tr><td>${r.item}</td><td>${r.pass ? '✓' : '✗'}</td><td>${r.note||''}</td></tr>`).join('')}</tbody></table>` : '';
    const runOutputBlock = execResult ? `<pre style="background:#f1f5f9;color:#111;border:1px solid #e2e8f0;">${(execResult.stdout||'').replace(/</g,'&lt;')}${execResult.stderr?`\n[stderr]\n${execResult.stderr.replace(/</g,'&lt;')}`:''}</pre>` : '';

    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>Lab ${labId} — Deliverable</title>
      <style>
        body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;padding:28px;color:#111;max-width:780px;margin:0 auto;}
        h1,h2,h3{line-height:1.25;margin-top:1.4em;}
        pre{background:#0f172a;color:#e2e8f0;padding:14px;border-radius:8px;overflow-x:auto;font-size:12.5px;line-height:1.5;white-space:pre-wrap;word-break:break-word;}
        code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12.5px;}
        table{border-collapse:collapse;margin-top:8px;width:100%;}
        td,th{border:1px solid #cbd5e1;padding:6px 10px;font-size:12.5px;text-align:left;}
        .meta{color:#555;font-size:12.5px;}
        .usage{background:#f1f5f9;border:1px solid #e2e8f0;border-radius:8px;padding:10px 12px;font-size:12.5px;color:#334155;margin-top:12px;}
        .footer{margin-top:40px;color:#64748b;font-size:11.5px;border-top:1px solid #e5e7eb;padding-top:12px;}
      </style></head><body>
      <h1>Lab ${labId} — ${labDef?.title || ''}</h1>
      <p class="meta">Manual steps ${labDef?.manual_step_range || ''} · Submitted by ${labResp.requester?.email || 'student'}</p>
      ${planBlock ? `<h2>Plan</h2>${planBlock}` : ''}
      ${section('Program', codeBlock(p.code || p.fixed_code || p.prototype_code))}
      ${p.explanation ? `<h2>Explanation</h2><div>${String(p.explanation).replace(/\n/g,'<br/>')}</div>` : ''}
      ${dxBlock ? `<h2>Diagnosis</h2>${dxBlock}` : ''}
      ${testsTable ? `<h2>Tests</h2>${testsTable}` : ''}
      ${p.readme ? `<h2>README</h2><pre style="background:#f8fafc;color:#111;border:1px solid #e2e8f0;">${String(p.readme).replace(/</g,'&lt;')}</pre>` : ''}
      ${chartsList ? `<h2>Charts</h2>${chartsList}` : ''}
      ${p.visualization_code ? `<h2>Visualization code</h2>${codeBlock(p.visualization_code)}` : ''}
      ${p.analysis ? `<h2>Analysis</h2><p><strong>Shape:</strong> ${p.analysis.dataset_shape}</p><ul>${(p.analysis.insights||[]).map(i=>`<li>${i}</li>`).join('')}</ul><p class="usage"><strong>Hand-verify:</strong> ${p.analysis.manual_verification_hint||''}</p>` : ''}
      ${slidesBlock ? `<h2>5-slide outline</h2>${slidesBlock}` : ''}
      ${p.executive_summary ? `<h2>Executive summary</h2><p>${p.executive_summary}</p>` : ''}
      ${raiBlock ? `<h2>Responsible AI checklist</h2>${raiBlock}` : ''}
      ${p.reflection_stub ? `<h2>Reflection draft (edit before submission)</h2><p><em>${p.reflection_stub}</em></p>` : ''}
      ${p.learning_note ? `<h2>Why the code failed (your reflection seed)</h2><p>${p.learning_note}</p>` : ''}
      ${runOutputBlock ? `<h2>Run output</h2>${runOutputBlock}` : ''}
      <p class="usage">Tokens used: ${u.input_tokens||0} in · ${u.output_tokens||0} out · ${u.provider||'n/a'} (${u.model||'?'}) · ${u.elapsed_ms||0} ms</p>
      <div class="footer">Generated via codefoundry.sh/playground?lab=${labId} · ${new Date().toLocaleString()}</div>
      </body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 400);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labResp, labDef, labId, execResult]);

  // Check autocomplete + execution availability
  useEffect(() => {
    fetch(`${API_URL}/api/autocomplete/status`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        setAutoCompleteAvailable(data.available);
        if (data.usage) setUsage(data.usage);
        if (data.plan) setUserPlan(data.plan);
      })
      .catch(() => setAutoCompleteAvailable(false));

    fetch(`${API_URL}/api/playground/execution-status`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setExecUsage({ used: data.used, quota: data.quota, remaining: data.remaining, percent_used: data.percent_used });
          setExecLimits(data.limits);
        }
      })
      .catch(() => {});

    // Lab 5 students → suppress upgrade / quota alerts entirely.
    // Admins also get the suppressed UX so they can preview exactly what
    // students see without logging in as one.
    fetch(`${API_URL}/api/labs/whoami`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(info => { if (info?.is_labs_access || info?.is_student) setIsStudent(true); })
      .catch(() => {});
  }, []);

  const handleLanguageChange = useCallback((lang) => {
    const opt = LANGUAGE_OPTIONS.find(l => l.value === lang);
    setLanguage(lang);
    setFilename(`playground${opt?.ext || '.txt'}`);
    setCode(STARTER_TEMPLATES[lang] || `// ${opt?.label || lang} playground\n\n`);
    setShowLangDropdown(false);
  }, []);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [code]);

  const handleDownload = useCallback(() => {
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, [code, filename]);

  const handleReset = useCallback(() => {
    setCode(STARTER_TEMPLATES[language] || '');
    setExecResult(null);
    setShowOutput(false);
  }, [language]);

  // Run code
  const handleRun = useCallback(async () => {
    setIsRunning(true);
    setShowOutput(true);
    setExecResult(null);

    try {
      const res = await fetch(`${API_URL}/api/playground/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          code: codeRef.current,
          language,
          filename,
        }),
      });

      if (res.status === 429) {
        const err = await res.json();
        setExecResult({
          stdout: '',
          stderr: err.detail || 'Execution limit reached',
          exit_code: -1,
          duration_ms: 0,
          timed_out: false,
          quota_error: true,
        });
      } else if (res.status === 403) {
        const err = await res.json();
        setExecResult({
          stdout: '',
          stderr: err.detail || 'Language not available on your plan',
          exit_code: -1,
          duration_ms: 0,
          timed_out: false,
          plan_error: true,
        });
      } else if (res.ok) {
        const data = await res.json();
        setExecResult(data);
        // Refresh execution usage
        fetch(`${API_URL}/api/playground/execution-status`, { credentials: 'include' })
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d) setExecUsage({ used: d.used, quota: d.quota, remaining: d.remaining, percent_used: d.percent_used }); })
          .catch(() => {});
      } else {
        const err = await res.json().catch(() => ({}));
        setExecResult({
          stdout: '',
          stderr: err.detail || `Error: HTTP ${res.status}`,
          exit_code: -1,
          duration_ms: 0,
          timed_out: false,
        });
      }
    } catch (e) {
      setExecResult({
        stdout: '',
        stderr: `Network error: ${e.message}`,
        exit_code: -1,
        duration_ms: 0,
        timed_out: false,
      });
    } finally {
      setIsRunning(false);
    }
  }, [language, filename]);

  // Keyboard shortcut: Ctrl+Enter to run
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (!isRunning) handleRun();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isRunning, handleRun]);

  // Drag to resize output panel
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = outputHeight;
    const onMove = (ev) => {
      const delta = startY - ev.clientY;
      setOutputHeight(Math.max(80, Math.min(500, startH + delta)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [outputHeight]);

  const isRunnable = LANGUAGE_OPTIONS.find(l => l.value === language)?.runnable;
  const canExecute = execLimits && execUsage && execUsage.remaining > 0;
  const langAllowed = execLimits?.languages === 'all' || (Array.isArray(execLimits?.languages) && execLimits.languages.includes(language));

  return (
    <div data-testid="playground-page" style={{ height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'row' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* Toolbar */}
      <div
        data-testid="playground-toolbar"
        style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.5rem 0.75rem',
          background: '#161b22', borderBottom: '1px solid #30363d',
          borderRadius: '0.75rem 0.75rem 0 0', flexWrap: 'wrap',
        }}
      >
        {/* Language selector */}
        <div style={{ position: 'relative' }}>
          <button
            data-testid="language-selector-btn"
            onClick={() => setShowLangDropdown(!showLangDropdown)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.375rem 0.625rem',
              background: '#21262d', border: '1px solid #30363d',
              borderRadius: '0.375rem', color: '#e6edf3',
              fontSize: '0.8125rem', cursor: 'pointer', fontFamily: 'IBM Plex Mono',
            }}
          >
            <FileCode size={14} style={{ color: '#58a6ff' }} />
            {LANGUAGE_OPTIONS.find(l => l.value === language)?.label || language}
            <ChevronDown size={12} style={{ opacity: 0.5 }} />
          </button>
          {showLangDropdown && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 50 }} onClick={() => setShowLangDropdown(false)} />
              <div data-testid="language-dropdown" style={{
                position: 'absolute', top: '100%', left: 0, zIndex: 51,
                marginTop: '0.25rem', minWidth: '180px',
                background: '#161b22', border: '1px solid #30363d',
                borderRadius: '0.5rem', padding: '0.25rem',
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                maxHeight: '300px', overflowY: 'auto',
              }}>
                {LANGUAGE_OPTIONS.map(opt => (
                  <button key={opt.value} data-testid={`lang-option-${opt.value}`}
                    onClick={() => handleLanguageChange(opt.value)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      width: '100%', textAlign: 'left',
                      padding: '0.375rem 0.625rem',
                      background: opt.value === language ? '#1c3049' : 'transparent',
                      border: 'none', color: '#e6edf3', fontSize: '0.8125rem',
                      cursor: 'pointer', borderRadius: '0.25rem', fontFamily: 'IBM Plex Mono',
                    }}
                    onMouseEnter={e => { if (opt.value !== language) e.target.style.background = '#21262d'; }}
                    onMouseLeave={e => { if (opt.value !== language) e.target.style.background = 'transparent'; }}
                  >
                    <span>{opt.label}<span style={{ marginLeft: '0.5rem', opacity: 0.4, fontSize: '0.75rem' }}>{opt.ext}</span></span>
                    {opt.runnable && <Play size={10} style={{ opacity: 0.3 }} />}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Filename */}
        <div style={{
          padding: '0.375rem 0.625rem', background: '#0d1117',
          border: '1px solid #21262d', borderRadius: '0.375rem',
          color: '#484f58', fontSize: '0.75rem', fontFamily: 'IBM Plex Mono',
        }}>
          {filename}
        </div>

        {/* Run button */}
        {isRunnable && (
          <button
            data-testid="run-code-btn"
            onClick={handleRun}
            disabled={isRunning || !canExecute || !langAllowed}
            title={
              !canExecute ? 'Execution quota exhausted'
              : !langAllowed ? `${language} not available on your plan`
              : isRunning ? 'Running...'
              : 'Run code (Ctrl+Enter)'
            }
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.375rem 0.875rem',
              background: isRunning ? '#21262d'
                : (!canExecute || !langAllowed) ? '#21262d'
                : '#238636',
              border: `1px solid ${isRunning || !canExecute || !langAllowed ? '#30363d' : '#2ea043'}`,
              borderRadius: '0.375rem',
              color: isRunning || !canExecute || !langAllowed ? '#484f58' : '#fff',
              fontSize: '0.8125rem', fontWeight: 600,
              cursor: isRunning || !canExecute || !langAllowed ? 'not-allowed' : 'pointer',
              fontFamily: 'IBM Plex Mono',
              transition: 'all 0.15s',
            }}
          >
            {isRunning ? (
              <><Square size={13} /> Running...</>
            ) : (
              <><Play size={13} /> Run</>
            )}
          </button>
        )}

        <div style={{ flex: 1 }} />

        {/* AI toggle */}
        <button
          data-testid="toggle-autocomplete-btn"
          onClick={() => setAutoCompleteEnabled(!autoCompleteEnabled)}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.375rem',
            padding: '0.375rem 0.625rem',
            background: autoCompleteEnabled ? 'rgba(63, 185, 80, 0.15)' : '#21262d',
            border: `1px solid ${autoCompleteEnabled ? 'rgba(63, 185, 80, 0.3)' : '#30363d'}`,
            borderRadius: '0.375rem',
            color: autoCompleteEnabled ? '#3fb950' : '#484f58',
            fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'IBM Plex Mono',
          }}
        >
          <Zap size={13} /> AI {autoCompleteEnabled ? 'ON' : 'OFF'}
        </button>

        {completionStatus !== 'idle' && autoCompleteEnabled && (
          <div style={{
            padding: '0.25rem 0.5rem', borderRadius: '0.25rem',
            fontSize: '0.6875rem', fontFamily: 'IBM Plex Mono',
            color: completionStatus === 'loading' ? '#58a6ff' : completionStatus === 'error' ? '#f85149' : '#3fb950',
            background: completionStatus === 'loading' ? 'rgba(88,166,255,0.1)' : completionStatus === 'error' ? 'rgba(248,81,73,0.1)' : 'rgba(63,185,80,0.1)',
          }}>
            {completionStatus === 'loading' ? 'Thinking...' : completionStatus === 'error' ? 'Error' : 'Ready'}
          </div>
        )}

        <div style={{ width: 1, height: 20, background: '#30363d' }} />

        {/* Action buttons */}
        {[
          { id: 'copy-code-btn', icon: copied ? <Check size={14} style={{ color: '#3fb950' }} /> : <Copy size={14} />, action: handleCopy, title: 'Copy code' },
          { id: 'download-code-btn', icon: <Download size={14} />, action: handleDownload, title: 'Download file' },
          { id: 'reset-code-btn', icon: <RotateCcw size={14} />, action: handleReset, title: 'Reset to template' },
        ].map(btn => (
          <button key={btn.id} data-testid={btn.id} onClick={btn.action} title={btn.title}
            style={{
              display: 'flex', alignItems: 'center', padding: '0.375rem 0.5rem',
              background: 'transparent', border: '1px solid #30363d',
              borderRadius: '0.375rem', color: '#8b949e', cursor: 'pointer',
              transition: 'color 0.15s, border-color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#e6edf3'; e.currentTarget.style.borderColor = '#484f58'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#8b949e'; e.currentTarget.style.borderColor = '#30363d'; }}
          >
            {btn.icon}
          </button>
        ))}
      </div>

      {/* Editor */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <CodeEditor
          key={`${language}-${autoCompleteEnabled}`}
          defaultValue={code}
          language={language}
          filename={filename}
          onChange={(val) => setCode(val || '')}
          enableCompletion={autoCompleteEnabled && autoCompleteAvailable && (!usage || usage.remaining > 0)}
          onStatusChange={setCompletionStatus}
        />
      </div>

      {/* Output Panel */}
      {showOutput && (
        <>
          {/* Drag handle */}
          <div
            ref={dragRef}
            onMouseDown={handleDragStart}
            data-testid="output-resize-handle"
            style={{
              height: '6px', cursor: 'ns-resize',
              background: '#21262d', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              borderTop: '1px solid #30363d',
            }}
          >
            <GripHorizontal size={14} style={{ color: '#484f58' }} />
          </div>

          <div
            data-testid="output-panel"
            style={{
              height: `${outputHeight}px`,
              background: '#0d1117',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Output header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.375rem 0.75rem',
              background: '#161b22', borderBottom: '1px solid #21262d',
              fontSize: '0.75rem', fontFamily: 'IBM Plex Mono',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Terminal size={13} style={{ color: '#8b949e' }} />
                <span style={{ color: '#e6edf3' }}>Output</span>
                {execResult && !isRunning && (
                  <>
                    <span style={{
                      padding: '1px 6px', borderRadius: '3px', fontSize: '0.6875rem',
                      background: execResult.exit_code === 0 ? 'rgba(63,185,80,0.15)' : 'rgba(248,81,73,0.15)',
                      color: execResult.exit_code === 0 ? '#3fb950' : '#f85149',
                    }}>
                      {execResult.exit_code === 0 ? 'Success' : `Exit: ${execResult.exit_code}`}
                    </span>
                    <span style={{ color: '#484f58', fontSize: '0.6875rem' }}>
                      {execResult.duration_ms}ms
                    </span>
                    {execResult.timed_out && (
                      <span style={{ color: '#d29922', display: 'flex', alignItems: 'center', gap: '2px', fontSize: '0.6875rem' }}>
                        <AlertTriangle size={11} /> Timed out
                      </span>
                    )}
                  </>
                )}
                {isRunning && (
                  <span style={{ color: '#58a6ff', fontSize: '0.6875rem', animation: 'pulse 1.5s infinite' }}>
                    Executing...
                  </span>
                )}
              </div>
              <button data-testid="close-output-btn" onClick={() => setShowOutput(false)} style={{
                background: 'transparent', border: 'none', color: '#484f58',
                cursor: 'pointer', padding: '2px', display: 'flex',
              }}
                onMouseEnter={e => e.currentTarget.style.color = '#e6edf3'}
                onMouseLeave={e => e.currentTarget.style.color = '#484f58'}
              >
                <X size={14} />
              </button>
            </div>

            {/* Output content */}
            <div style={{
              flex: 1, overflow: 'auto', padding: '0.5rem 0.75rem',
              fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.8125rem',
              lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {isRunning && !execResult && (
                <div style={{ color: '#58a6ff' }}>Running {language} code...</div>
              )}
              {execResult?.quota_error && !isStudent && (
                <div style={{ color: '#f85149' }}>
                  {execResult.stderr}
                  <div style={{ marginTop: '0.75rem' }}>
                    <button data-testid="upgrade-from-output-btn" onClick={() => navigate('/pricing')}
                      style={{
                        padding: '0.375rem 1rem', background: '#238636', color: '#fff',
                        border: 'none', borderRadius: '0.375rem', fontSize: '0.8125rem',
                        fontWeight: 600, cursor: 'pointer',
                      }}
                    >Upgrade Plan</button>
                  </div>
                </div>
              )}
              {execResult?.quota_error && isStudent && (
                <div style={{ color: '#d29922' }} data-testid="student-quota-note">
                  You've used all 5 Lab 5 attempts this period. New attempts unlock after 30 days.
                </div>
              )}
              {execResult?.plan_error && !isStudent && (
                <div style={{ color: '#d29922' }}>
                  {execResult.stderr}
                  <div style={{ marginTop: '0.5rem', color: '#8b949e', fontSize: '0.75rem' }}>
                    Upgrade to Builder ($149/mo) or higher for all language support.
                  </div>
                </div>
              )}
              {execResult && !execResult.quota_error && !execResult.plan_error && (
                <>
                  {execResult.stdout && <div style={{ color: '#e6edf3' }}>{execResult.stdout}</div>}
                  {execResult.stderr && (
                    <div style={{ color: execResult.exit_code === 0 ? '#d29922' : '#f85149', marginTop: execResult.stdout ? '0.25rem' : 0 }}>
                      {execResult.stderr}
                    </div>
                  )}
                  {!execResult.stdout && !execResult.stderr && (
                    <div style={{ color: '#484f58', fontStyle: 'italic' }}>No output</div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Quota exceeded overlay — hidden for whitelisted students (Lab 5) */}
      {usage && usage.remaining <= 0 && userPlan !== 'enterprise' && !isStudent && (
        <div data-testid="quota-exceeded-banner" style={{
          padding: '1rem 1.25rem', background: '#161b22',
          borderTop: '1px solid #30363d',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Lock size={16} style={{ color: '#f85149' }} />
            <span style={{ color: '#f85149', fontSize: '0.8125rem', fontWeight: 600 }}>
              Monthly completion quota reached ({usage.quota?.toLocaleString()})
            </span>
          </div>
          <button data-testid="upgrade-plan-btn" onClick={() => navigate('/pricing')} style={{
            padding: '0.375rem 1rem', background: 'hsl(174, 68%, 35%)',
            color: '#fff', border: 'none', borderRadius: '0.375rem',
            fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer',
          }}>Upgrade Plan</button>
        </div>
      )}

      {/* Status bar */}
      <div
        data-testid="playground-statusbar"
        style={{
          display: 'flex', alignItems: 'center', gap: '1rem',
          padding: '0.25rem 0.75rem',
          background: '#161b22', borderTop: '1px solid #21262d',
          borderRadius: '0 0 0.75rem 0.75rem',
          fontSize: '0.6875rem', fontFamily: 'IBM Plex Mono', color: '#484f58',
        }}
      >
        <span>{LANGUAGE_OPTIONS.find(l => l.value === language)?.label}</span>
        <span>UTF-8</span>
        <span>{code.split('\n').length} lines</span>

        {/* Completion usage */}
        {usage && usage.quota > 0 && (
          <div data-testid="completion-usage-meter" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Zap size={10} />
            <span style={{
              color: usage.percent_used > 90 ? '#f85149' : usage.percent_used > 70 ? '#d29922' : '#3fb950',
            }}>{usage.used?.toLocaleString()} / {usage.quota?.toLocaleString()}</span>
            <div style={{ width: '40px', height: '3px', background: '#21262d', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{
                width: `${Math.min(usage.percent_used, 100)}%`, height: '100%',
                background: usage.percent_used > 90 ? '#f85149' : usage.percent_used > 70 ? '#d29922' : '#3fb950',
                borderRadius: '2px',
              }} />
            </div>
          </div>
        )}

        {/* Execution usage */}
        {execUsage && execUsage.quota > 0 && (
          <div data-testid="execution-usage-meter" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Terminal size={10} />
            <span style={{
              color: execUsage.percent_used > 90 ? '#f85149' : execUsage.percent_used > 70 ? '#d29922' : '#3fb950',
            }}>{execUsage.used} / {execUsage.quota?.toLocaleString()} runs</span>
          </div>
        )}

        <div style={{ flex: 1 }} />

        {execLimits && (
          <span style={{ opacity: 0.5 }}>
            Timeout: {execLimits.timeout_sec}s | Max concurrent: {execLimits.max_concurrent}
          </span>
        )}
        <span style={{ opacity: 0.5 }}>Ctrl+Enter to run | Tab to accept AI</span>
      </div>
      </div>{/* /left-column */}

      {/* ─────────── Lab Agent sidebar (only when ?lab=N) ─────────── */}
      {labId && (
        <aside
          data-testid={`lab-agent-panel-${labId}`}
          style={{
            width: 420, maxWidth: '45vw', minWidth: 320,
            borderLeft: '1px solid #30363d', background: '#0d1117',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}
        >
          {/* Panel header */}
          <div style={{ padding: '0.75rem 0.9rem', borderBottom: '1px solid #21262d', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#58a6ff', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <Sparkles size={11} /> Lab {labId} · {labDef?.manual_step_range ? `steps ${labDef.manual_step_range}` : '…'}
              </div>
              <div style={{ marginTop: 2, color: '#e6edf3', fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.3 }}>
                {labDef?.title || 'Loading…'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/playground')}
              title="Exit lab mode"
              data-testid="lab-exit-btn"
              style={{ background: 'transparent', border: '1px solid #30363d', color: '#8b949e', borderRadius: 6, padding: '0.2rem 0.4rem', cursor: 'pointer' }}
            >
              <X size={13} />
            </button>
          </div>

          {/* Scrollable body */}
          <div style={{ flex: 1, overflow: 'auto', padding: '0.75rem 0.9rem' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#8b949e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Your prompt</span>
              <button
                type="button"
                onClick={loadLabStarter}
                data-testid="lab-starter-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.15rem 0.5rem', fontSize: '0.6875rem', color: '#58a6ff', background: 'transparent', border: '1px solid #30363d', borderRadius: 5, cursor: 'pointer' }}
              >
                <Shuffle size={10} /> Starter
              </button>
            </label>
            <textarea
              value={labPrompt}
              onChange={(e) => setLabPrompt(e.target.value)}
              placeholder={labDef?.starter_prompts?.[0] || 'Describe what you want the agent to build…'}
              rows={5}
              data-testid="lab-prompt-input"
              style={{
                marginTop: 6, width: '100%', padding: '0.6rem 0.7rem', borderRadius: 6,
                border: '1px solid #30363d', background: '#010409', color: '#e6edf3',
                fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.8125rem', lineHeight: 1.5, resize: 'vertical',
              }}
            />
            <button
              type="button"
              onClick={askLabAgent}
              disabled={labLoading || labPrompt.trim().length < 4}
              data-testid="lab-ask-btn"
              style={{
                marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.4rem 0.9rem',
                background: '#238636', border: '1px solid #2ea043', borderRadius: 6, color: '#fff',
                cursor: (labLoading || labPrompt.trim().length < 4) ? 'not-allowed' : 'pointer',
                opacity: (labLoading || labPrompt.trim().length < 4) ? 0.6 : 1, fontSize: '0.8125rem', fontWeight: 600, fontFamily: 'IBM Plex Mono',
              }}
            >
              {labLoading ? <Loader2 size={13} className="spin-lab" /> : <Sparkles size={13} />}
              {labLoading ? 'Asking the agent…' : 'Ask the agent'}
            </button>
            {labError && (
              <div data-testid="lab-error" style={{ marginTop: 8, color: '#f85149', fontSize: '0.75rem' }}>{labError}</div>
            )}

            {/* Response */}
            {labResp && (
              <div style={{ marginTop: 14 }} data-testid="lab-response">
                {(labResp.parsed?.code || labResp.parsed?.fixed_code || labResp.parsed?.visualization_code || labResp.parsed?.prototype_code) && (
                  <div style={{ marginBottom: 10 }}>
                    <button
                      type="button"
                      onClick={insertLabCodeIntoEditor}
                      data-testid="lab-insert-code-btn"
                      style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0.45rem 0.75rem', background: '#1c3049', border: '1px solid #388bfd', borderRadius: 6, color: '#58a6ff', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600, fontFamily: 'IBM Plex Mono' }}
                    >
                      <ArrowRight size={12} /> Insert code into editor
                    </button>
                    <div style={{ marginTop: 4, color: '#484f58', fontSize: '0.6875rem', textAlign: 'center' }}>
                      Then press <strong>Run</strong> above to execute.
                    </div>
                  </div>
                )}

                {labResp.parsed?.diagnosis && (
                  <LabSection title="Diagnosis">
                    <LabDxBlock dx={labResp.parsed.diagnosis} />
                  </LabSection>
                )}
                {labResp.parsed?.explanation && (
                  <LabSection title="Explanation">
                    <div className="lab-md"><ReactMarkdown>{labResp.parsed.explanation}</ReactMarkdown></div>
                  </LabSection>
                )}
                {Array.isArray(labResp.parsed?.tests) && labResp.parsed.tests.length > 0 && (
                  <LabSection title={`Tests (${labResp.parsed.tests.length})`}>
                    <LabTestsList tests={labResp.parsed.tests} />
                  </LabSection>
                )}
                {labResp.parsed?.readme && (
                  <LabSection title="README">
                    <div className="lab-md" style={{ background: '#161b22', padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid #21262d' }}>
                      <ReactMarkdown>{labResp.parsed.readme}</ReactMarkdown>
                    </div>
                  </LabSection>
                )}
                {Array.isArray(labResp.parsed?.charts) && labResp.parsed.charts.length > 0 && (
                  <LabSection title="Charts">
                    <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#c9d1d9' }}>
                      {labResp.parsed.charts.map(c => (
                        <li key={c.index} style={{ marginBottom: 4 }}>
                          <strong>{(c.type || '').toUpperCase()}</strong> — {c.question}
                        </li>
                      ))}
                    </ul>
                    {labResp.parsed.best_chart && (
                      <div style={{ marginTop: 6, color: '#8b949e', fontSize: '0.75rem' }}>
                        <strong>Best:</strong> #{labResp.parsed.best_chart.index} — {labResp.parsed.best_chart.justification}
                      </div>
                    )}
                  </LabSection>
                )}
                {labResp.parsed?.plan && (
                  <LabSection title="Capstone plan">
                    <div style={{ color: '#c9d1d9' }}>
                      <div><strong>Problem:</strong> {labResp.parsed.plan.problem}</div>
                      <div><strong>Users:</strong> {labResp.parsed.plan.users}</div>
                      <ul style={{ margin: '6px 0 0', paddingLeft: '1.2rem' }}>
                        {(labResp.parsed.plan.requirements || []).map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    </div>
                  </LabSection>
                )}
                {Array.isArray(labResp.parsed?.slide_outline) && labResp.parsed.slide_outline.length > 0 && (
                  <LabSection title="5-slide outline">
                    {labResp.parsed.slide_outline.map(s => (
                      <div key={s.slide} style={{ marginBottom: 6, color: '#c9d1d9' }}>
                        <strong>Slide {s.slide}: {s.title}</strong>
                        <ul style={{ margin: '2px 0', paddingLeft: '1.2rem' }}>{(s.bullets || []).map((b, i) => <li key={i}>{b}</li>)}</ul>
                      </div>
                    ))}
                  </LabSection>
                )}
                {Array.isArray(labResp.parsed?.responsible_ai_checklist) && labResp.parsed.responsible_ai_checklist.length > 0 && (
                  <LabSection title="Responsible AI checklist">
                    <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#c9d1d9' }}>
                      {labResp.parsed.responsible_ai_checklist.map((r, i) => (
                        <li key={i}>{r.pass ? '✓' : '✗'} {r.item} — <em style={{ color: '#8b949e' }}>{r.note}</em></li>
                      ))}
                    </ul>
                  </LabSection>
                )}
                {labResp.parsed?.learning_note && (
                  <LabSection title="Reflection seed"><p style={{ margin: 0, color: '#c9d1d9', fontStyle: 'italic' }}>{labResp.parsed.learning_note}</p></LabSection>
                )}
                {labResp.parsed?.reflection_stub && (
                  <LabSection title="Reflection draft"><p style={{ margin: 0, color: '#c9d1d9', fontStyle: 'italic' }}>{labResp.parsed.reflection_stub}</p></LabSection>
                )}
                {/* Fallback raw */}
                {(!labResp.parsed || Object.keys(labResp.parsed).length === 0) && labResp.raw && (
                  <LabSection title="Agent response"><pre style={{ whiteSpace: 'pre-wrap', color: '#c9d1d9', fontSize: '0.75rem' }}>{labResp.raw}</pre></LabSection>
                )}

                <div style={{ marginTop: 12, padding: '8px 10px', background: '#161b22', border: '1px solid #21262d', borderRadius: 6, fontSize: '0.7rem', color: '#8b949e' }} data-testid="lab-usage">
                  Tokens: <strong style={{ color: '#c9d1d9' }}>{labResp.usage?.input_tokens || 0}</strong> in · <strong style={{ color: '#c9d1d9' }}>{labResp.usage?.output_tokens || 0}</strong> out · via <strong style={{ color: '#58a6ff' }}>{labResp.usage?.provider || 'n/a'}</strong> ({labResp.usage?.model || '?'}) · {labResp.usage?.elapsed_ms || 0} ms
                </div>

                <button
                  type="button"
                  onClick={printLabDeliverable}
                  data-testid="lab-print-btn"
                  style={{ marginTop: 10, width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0.45rem 0.75rem', background: 'transparent', border: '1px solid #30363d', borderRadius: 6, color: '#c9d1d9', cursor: 'pointer', fontSize: '0.8125rem', fontFamily: 'IBM Plex Mono' }}
                >
                  <Printer size={12} /> Save submission PDF
                </button>
              </div>
            )}

            {!labResp && !labLoading && labDef && (
              <div style={{ marginTop: 14, padding: '10px 12px', background: '#161b22', border: '1px dashed #30363d', borderRadius: 6, color: '#8b949e', fontSize: '0.75rem', lineHeight: 1.5 }}>
                <strong style={{ color: '#c9d1d9' }}>How this lab works:</strong><br/>
                1. Click <em>Starter</em> or type your own prompt.<br/>
                2. Press <em>Ask the agent</em> — ONE AI call, no 3-agent orchestration.<br/>
                3. Click <em>Insert code into editor</em>, then <em>Run</em>.<br/>
                4. Click <em>Save submission PDF</em> for your deliverable.
              </div>
            )}
          </div>
          <style>{`
            .spin-lab { animation: labspin 0.9s linear infinite; }
            @keyframes labspin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
            .lab-md p { margin: 0.3em 0; color: #c9d1d9; font-size: 0.8125rem; line-height: 1.55; }
            .lab-md ul { padding-left: 1.1rem; margin: 0.3em 0; color: #c9d1d9; font-size: 0.8125rem; }
            .lab-md code { background: #161b22; padding: 1px 5px; border-radius: 4px; font-size: 0.85em; color: #e6edf3; }
          `}</style>
        </aside>
      )}
    </div>
  );
}

// ─── Lab sidebar helpers ───
function LabSection({ title, children }) {
  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #21262d' }}>
      <div style={{ color: '#8b949e', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: '0.8125rem', lineHeight: 1.5, color: '#c9d1d9' }}>{children}</div>
    </div>
  );
}
function LabDxBlock({ dx }) {
  const row = (label, obj) => obj && (
    <div style={{ marginBottom: 6 }}>
      <strong>{label}</strong> <span style={{ color: '#8b949e' }}>(line {obj.line_hint ?? '?'})</span>
      <div><em>Symptom:</em> {obj.symptom}</div>
      <div><em>Cause:</em> {obj.root_cause}</div>
    </div>
  );
  return <>{row('Syntax bug', dx.syntax_bug)}{row('Logic bug', dx.logic_bug)}</>;
}
function LabTestsList({ tests }) {
  return (
    <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
      {tests.map((t, i) => (
        <li key={i} style={{ marginBottom: 4 }}>
          <code style={{ color: '#58a6ff' }}>{t.category || 'test'}</code>: <code>{t.input ?? t.assertion ?? ''}</code>
          {t.expected && <> → <code>{t.expected}</code></>}
        </li>
      ))}
    </ul>
  );
}
